import React from 'react';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { eventData } from '../mockData';
import { useEventDialog } from '../context/EventDialogContext';
import AnimatedSun from '../components/AnimatedSun';
import Countdown from '../components/Countdown';
import ConfirmedCounter from '../components/ConfirmedCounter';

const Home = () => {
  const { openInspiration, openRSVP } = useEventDialog();

  return (
    <div className="min-h-screen">
      {/* Hero Section - taller, content spread top & bottom so the sun breathes */}
      <section className="relative min-h-[140vh] flex flex-col items-center justify-between overflow-hidden bg-black py-20 md:py-32 px-4">
        {/* Animated Sun Background - centered */}
        <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
          <AnimatedSun />
        </div>

        {/* TOP GROUP : invitation + title */}
        <div className="relative z-10 text-center max-w-5xl mx-auto">
          <p
            className="text-sm md:text-base text-amber-100/85 max-w-2xl mx-auto mb-10 leading-relaxed uppercase tracking-[0.15em]"
            style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 300 }}
          >
            Le comte et la comtesse Bruno de Limburg Stirum ont le plaisir de vous inviter à la soirée qu'ils donneront pour leur fille Rose.
          </p>

          <h1 className="text-7xl md:text-9xl font-black tracking-wider mb-4 mad-max-title">
            {eventData.title}
          </h1>
          {eventData.subtitle && (
            <p className="text-xl md:text-2xl text-amber-200 font-medium tracking-wide uppercase mt-4">
              {eventData.subtitle}
            </p>
          )}
        </div>

        {/* MIDDLE : empty - let the sun breathe */}
        <div className="relative z-10"></div>

        {/* BOTTOM GROUP : info pills + banner + CTAs + countdown + tag line */}
        <div className="relative z-10 text-center w-full max-w-5xl mx-auto flex flex-col items-center gap-7">
          {/* Info pills */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-3 bg-black/50 backdrop-blur-sm px-5 py-3 rounded-full border border-orange-600/30">
              <Calendar className="w-5 h-5 text-orange-500" />
              <span className="text-white font-medium text-sm md:text-base">{eventData.date}</span>
            </div>
            <div className="flex items-center gap-3 bg-black/50 backdrop-blur-sm px-5 py-3 rounded-full border border-orange-600/30">
              <Clock className="w-5 h-5 text-orange-500" />
              <span className="text-white font-medium text-sm md:text-base">{eventData.time}</span>
            </div>
            <a
              href="https://www.google.com/maps/?q=50.800611,4.575417"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-link"
              className="flex items-center gap-3 bg-black/50 backdrop-blur-sm px-5 py-3 rounded-full border border-orange-600/30 hover:border-orange-500 hover:bg-black/70 transition-all duration-300 cursor-pointer"
            >
              <MapPin className="w-5 h-5 text-orange-500" />
              <span className="text-white font-medium text-sm md:text-base">{eventData.location.name}</span>
            </a>
          </div>

          {/* Info Banner - Arrival time + Game start */}
          <div className="inline-flex flex-col md:flex-row items-center gap-2 md:gap-4 px-4 py-2 rounded-full bg-gradient-to-r from-orange-600/20 via-amber-600/20 to-orange-600/20 backdrop-blur-sm border border-orange-500/40">
            <div className="flex items-center gap-2">
              <span className="text-orange-300 text-[10px] md:text-xs font-bold uppercase tracking-widest">Arrivée</span>
              <span className="text-white font-bold text-sm md:text-base">18h pile</span>
            </div>
            <span className="hidden md:inline text-orange-500/60">•</span>
            <div className="flex items-center gap-2">
              <span className="text-orange-300 text-[10px] md:text-xs font-bold uppercase tracking-widest">Le jeu</span>
              <span className="text-white font-bold text-sm md:text-base">démarre à 18h30 ⚡</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={openRSVP}
              data-testid="hero-rsvp-btn"
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold px-8 py-6 text-lg border-2 border-orange-400 shadow-lg shadow-orange-500/50 transition-all duration-300 hover:scale-105"
            >
              Confirmer ma présence
            </Button>
            <Button
              onClick={openInspiration}
              data-testid="hero-inspiration-btn"
              size="lg"
              variant="outline"
              className="bg-black/60 backdrop-blur-sm border-2 border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
            >
              Dress code
            </Button>
          </div>

          {/* Countdown */}
          <div className="flex flex-col items-center gap-3">
            <Countdown />
            <ConfirmedCounter />
          </div>

          {/* MAGELLAN tag line */}
          <p
            className="text-[10px] md:text-xs text-amber-100/60 mt-4 uppercase tracking-[0.4em]"
            style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 300 }}
          >
            MAGELLAN — SKYLINE — ASAP — NO BORDERS
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
